/** The role claim as the API's token writes it (ClaimTypes.Role, shortened to "role" or kept as the full URI). */
const ROLE_CLAIMS = ['role', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

/**
 * The signed-in user's role codes (ADMIN, TCMGR, ...), read from the role claims in their token. The token is
 * signed by the API and every endpoint checks the role again, so this only decides what the SPA shows. Returns
 * an empty list for no token or one that cannot be read.
 */
export function currentUserRoles(): string[] {
  try {
    const token: string | undefined = JSON.parse(localStorage.getItem('currentUser') || 'null')?.token;
    const payload = token?.split('.')[1];
    if (!payload) {
      return [];
    }
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=')));
    return ROLE_CLAIMS.flatMap(claim => {
      const value = json[claim];
      return Array.isArray(value) ? value : value ? [value] : [];
    }).map(r => String(r).toUpperCase());
  } catch {
    return [];
  }
}

/** True when the signed-in user holds the role (case-insensitive). */
export function hasRole(role: string): boolean {
  return currentUserRoles().includes(role.toUpperCase());
}

/** The client role codes (Role.RoleTypeId 3): a user holding only these is a client user. */
export const CLIENT_ROLE_CODES = ['CLIENT', 'CLREPORTS', 'CUSTODIAN', 'ASSESSOR'];

/**
 * True when the signed-in user is a client user: they hold at least one role and every role is a client role.
 * Anyone holding an internal role as well keeps the internal view, as on the API (CallerClientScope).
 */
export function isClientUser(): boolean {
  const roles = currentUserRoles();
  return roles.length > 0 && roles.every(r => CLIENT_ROLE_CODES.includes(r));
}

/** The internal staff role codes: consultants, coordinators, programme managers, QA, reporting and admin. */
export const INTERNAL_STAFF_ROLE_CODES = ['ADMIN', 'TCMGR', 'TTRCRD', 'PRGMGR', 'QA', 'RPTUSER'];

/**
 * True when the signed-in user holds an internal staff role. A tutor (STAFF) does not, so text that talks about
 * pay, payroll or Orbit is shown only when this is true.
 */
export function isInternalStaff(): boolean {
  return currentUserRoles().some(r => INTERNAL_STAFF_ROLE_CODES.includes(r));
}